// src/components/Auth/ReferralRegister.js
import React, { useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import RegisterForm from "./forms/RegisterForm";
import VerifyForm from "./forms/VerifyForm";
import "./referralRegister.scss";
import { Button, Modal } from "antd";
import {
  BadgeDollarSign,
  CheckCircle2,
  Gift,
  PlaneTakeoff,
  ShieldCheck,
  Smartphone,
  Sparkles,
  TicketPercent,
} from "lucide-react";

function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
}

export default function ReferralRegister() {
    const params = useParams();
    const query = useQuery();

    const [step, setStep] = useState("register");
    const [verifyInfo, setVerifyInfo] = useState(null);
    const [isOpen, setIsOpen] = useState(false);

    const codeFromParam = params.referralCode;
    const codeFromQuery =
        query.get("code") ||
        query.get("ref") ||
        query.get("referral") ||
        query.get("referralCode");

    const referralCode = (codeFromParam || codeFromQuery || "").trim();

    return (
      <div className="referral-register-page">
        <section className="referral-hero-panel" aria-label="Teleport Now referral reward">
          <Link to="/" className="referral-brand">
            <img src="/images/Android.png" alt="Teleport Now" />
            <span>Teleport Now</span>
          </Link>

          <div className="referral-hero-content">
            <p className="referral-eyebrow">Referral reward</p>
            <h1>Your friend invited you to travel smarter.</h1>
            <p>
              Create your Teleport Now account, make your first purchase, and both
              of you unlock travel credit for the next trip.
            </p>
          </div>

          <div className="referral-reward-card">
            <div className="referral-reward-card__icon">
              <Gift size={26} />
            </div>
            <div>
              <span>You get</span>
              <strong>$3 USD credit</strong>
            </div>
            <div>
              <span>Your friend gets</span>
              <strong>$3 USD credit</strong>
            </div>
          </div>

          <div className="referral-benefits">
            <div>
              <Smartphone size={20} />
              <span>Travel eSIMs in minutes</span>
            </div>
            <div>
              <TicketPercent size={20} />
              <span>App-only deals</span>
            </div>
            <div>
              <ShieldCheck size={20} />
              <span>Secure checkout</span>
            </div>
          </div>
        </section>

        <section className="referral-register-card" aria-label="Referral signup form">
          <div className="referral-card-header">
            <div className="referral-card-kicker">
              <Sparkles size={17} />
              <span>Claim your invite</span>
            </div>
            <h2>{step === "register" ? "Create your account" : "Verify your email"}</h2>
            <p>
              {step === "register"
                ? "Your referral code is ready. Sign up to lock in the reward."
                : "Enter the one-time code we sent to your inbox."}
            </p>
            <div className="referral-code-chip">
              <BadgeDollarSign size={17} />
              <span>{referralCode || "Referral code optional"}</span>
            </div>
          </div>

          <div className="referral-form-shell">
            {step === "register" && (
              <RegisterForm
                key={referralCode || "no-referral-code"}
                referralCode={referralCode}
                goToOtpVerify={(userId, email, password) => {
                  setVerifyInfo({ userId, email, password });
                  setStep("verify");
                }}
              />
            )}

            {step === "verify" && (
              <VerifyForm
                verifyInfo={verifyInfo}
                verifyOnSucess={() =>{setStep("register");setIsOpen(true);}}/>
            )}
          </div>

          {step === "register" && (
            <p className="referral-signin-note">
              Already have an account? <Link to="/login">Sign in</Link>
            </p>
          )}

          <div className="referral-card-footer">
            <CheckCircle2 size={16} />
            <span>Reward applies after the first completed purchase.</span>
          </div>
        </section>

        <Modal
          open={isOpen}
          title={<p className="referral-popup-title">Referral locked in</p>}
          onOk={() => setIsOpen(false)}
          onCancel={() => setIsOpen(false)}
          maskClosable={true}
          closable={true}
          className="referral-popup"
          footer={[
            <Button
              key="submit"
              onClick={() => setIsOpen(false)}
              className="referral-popup-btn"
            >
              Got it
            </Button>,
          ]}
        >
          <div className="referral-popup-content">
            <div className="referral-popup-icon">
              <PlaneTakeoff size={30} />
            </div>
            <p>
              Complete a purchase and you and your friend each get $3 USD travel
              credit. Download the app, sign in, and start planning the next trip.
            </p>

            <div className="store-buttons">
              <a
                className="app-store-button"
                href="https://apps.apple.com/sg/app/teleport-now/id6756201717"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/images/appstore.svg" alt="App Store" />
              </a>

              <a
                className="play-store-button"
                href="https://play.google.com/store/apps/details?id=sg.teleportnow.app"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/images/playstore.svg" alt="Google Play Store" />
              </a>
            </div>
          </div>
        </Modal>
      </div>
    );
}
